import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchRealTimeWarehouseActivityThunk,
  fetchOrganizationThunk,
} from "../../store/reports/thunk";
import { setDate } from "../../store/reports/actions";
import AsyncStorage from "@react-native-async-storage/async-storage";
import "../../styles/global.css";
import noDataImage from "../../assets/no_data.png";
import {
  Home,
  PauseCircle,
  PlayCircle,
  ChevronLeftCircle,
  ChevronRightCircle,
} from "lucide-react";
import { APP_CONSTANTS } from "../../utilities/app_constants";

const RealTimeWarehouseActivity = ({
  reportData,
  onNext,
  onPrevious,
  onPause,
  isPaused,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [params, setParams] = useState({});
  const [organizationFetched, setOrganizationFetched] = useState(false);
  const [orgLoading, setOrgLoading] = useState(false);

  const {
    realTimeWarehouseActivity,
    currentDate,
    loading,
    error,
    organizationData,
  } = useSelector((state) => state.report);

  useEffect(() => {
    if (reportData?.url) {
      const urlParams = new URLSearchParams(reportData.url.split("?")[1]);
      const extractedParams = {
        orgId: urlParams.get("AD_Org_ID"),
        note: urlParams.get("Note"),
      };
      setParams(extractedParams);
    }
  }, [reportData]);

  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Main data fetch
  useEffect(() => {
    if (params.orgId) {
      const date = getCurrentDate();
      dispatch(setDate(date));
      dispatch(fetchRealTimeWarehouseActivityThunk(params.orgId));
    }
  }, [dispatch, params]);

  // Get organization name from the response
  const getOrganizationName = () => {
    console.log("Getting organization name from:", organizationData);
    
    if (!organizationData) return null;
    
    // Try different response structures
    if (organizationData.response?.data?.[0]?.name) {
      return organizationData.response.data[0].name;
    }
    if (organizationData.data?.[0]?.name) {
      return organizationData.data[0].name;
    }
    if (organizationData.name) {
      return organizationData.name;
    }
    if (organizationData.response?.name) {
      return organizationData.response.name;
    }
    
    return null;
  };

  // Fetch organization when warehouse is null
  useEffect(() => {
    const warehouse = realTimeWarehouseActivity?.data?.[0]?.Warehouse;
    
    console.log("Checking warehouse:", { warehouse, orgId: params.orgId });
    
    // Check if warehouse is null/undefined AND we have orgId
    if ((!warehouse || warehouse === null || warehouse === "") && 
        params.orgId &&
        !organizationFetched &&
        !orgLoading) {
      console.log("Warehouse is null. Fetching organization for ID:", params.orgId);
      setOrgLoading(true);
      dispatch(fetchOrganizationThunk(params.orgId));
      setOrganizationFetched(true);
    }
  }, [realTimeWarehouseActivity, params.orgId, dispatch, organizationFetched, orgLoading]);

  // Determine display values
  const warehouse = realTimeWarehouseActivity?.data?.[0]?.Warehouse;
  const organizationName = getOrganizationName();
  const isWarehouseNull = (!warehouse || warehouse === null || warehouse === "");
  
  console.log("Display decision:", { 
    isWarehouseNull, 
    organizationName, 
    orgLoading,
    warehouse 
  });
  
  let displayWarehouse;
  let warehouseLabel = "Warehouse:";
  
  if (!isWarehouseNull) {
    // Show actual warehouse
    displayWarehouse = warehouse;
    warehouseLabel = "Warehouse:";
  } else if (organizationName) {
    // Show organization name when warehouse is null
    displayWarehouse = organizationName;
    warehouseLabel = "Organization:";
  } else if (orgLoading) {
    displayWarehouse = "Loading...";
    warehouseLabel = "Organization:";
  } else {
    displayWarehouse = "-";
    warehouseLabel = "Warehouse:";
  }

  // Auto-Navigate after the specified interval if not paused
  useEffect(() => {
    if (!isPaused) {
      const intervalInSeconds = parseInt(reportData?.interval, 10);
      const displayTime =
        !isNaN(intervalInSeconds) && intervalInSeconds > 0
          ? intervalInSeconds * 1000
          : APP_CONSTANTS.REPORT_DISPLAY_TIME;

      const timeout = setTimeout(() => {
        onNext();
      }, displayTime);

      return () => clearTimeout(timeout);
    }
  }, [onNext, isPaused, reportData?.interval]);

  // Add responsive layout styles WITHOUT changing UI appearance
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      /* ONLY RESPONSIVE LAYOUT CHANGES - NO UI/COLOR CHANGES */
      
      /* Container responsiveness */
      .container {
        max-width: 100%;
        overflow-x: hidden;
      }
      
      /* Header responsive layout */
      @media (max-width: 1024px) {
        .header {
          flex-wrap: wrap !important;
          justify-content: center !important;
        }
        
        .header .home-button {
          order: 1;
        }
        
        .header .controls {
          order: 2;
        }
        
        .header h1 {
          order: 3;
          width: 100%;
          text-align: center;
          margin: 10px 0 !important;
        }
        
        .header .header-info {
          order: 4;
          width: 100%;
          justify-content: center !important;
          margin: 8px 0 !important;
        }
        
        .header .date-display {
          order: 5;
          margin: 8px auto !important;
        }
        
        .header .logo-container {
          order: 6;
          margin: 5px 0 !important;
        }
      }
      
      @media (max-width: 768px) {
        .header .controls svg {
          width: 32px !important;
          height: 32px !important;
        }
        
        .header h1 {
          font-size: 18px !important;
        }
        
        .current-date {
          width: 70px !important;
          font-size: 11px !important;
        }
      }
      
      @media (max-width: 480px) {
        .header .controls {
          gap: 5px !important;
        }
        
        .header .controls svg {
          width: 28px !important;
          height: 28px !important;
        }
        
        .header h1 {
          font-size: 16px !important;
        }
        
        .current-date {
          width: 60px !important;
          font-size: 10px !important;
        }
      }
      
      /* Table responsiveness - horizontal scroll on small screens */
      .table-container {
        overflow-x: auto;
        width: 100%;
      }
      
      .data-table {
        min-width: 800px;
      }
      
      /* Responsive table styles */
      @media (max-width: 768px) {
        .data-table {
          font-size: 12px;
        }
        
        .data-table th,
        .data-table td {
          padding: 6px !important;
        }
        
        .header-column {
          font-size: 12px !important;
          min-width: 120px !important;
        }
      }
      
      @media (max-width: 480px) {
        .data-table th,
        .data-table td {
          padding: 4px !important;
          font-size: 10px !important;
        }
        
        .header-column {
          font-size: 10px !important;
          min-width: 100px !important;
        }
      }
      
      /* Marquee responsiveness */
      @media (max-width: 768px) {
        .marquee-container {
          font-size: 14px !important;
          bottom: 20px !important;
        }
        
        .marquee-container div span {
          padding-right: 60px !important;
        }
      }
      
      @media (max-width: 480px) {
        .marquee-container {
          font-size: 11px !important;
          bottom: 10px !important;
          padding: 8px 0 !important;
        }
        
        .marquee-container div span {
          padding-right: 40px !important;
        }
      }
      
      /* Keep ALL original table styles exactly as they were */
      .table-layout {
        width: 100%;
      }
      
      .data-table {
        border-collapse: collapse;
        width: 100%;
      }
      
      .data-table th,
      .data-table td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
      }
      
      .data-table th {
        background-color: linear-gradient(135deg, #0a40e5 0%, #2484f1 100%);
        font-weight: bold;
          color:#fff;
      }
      
      .header-column {
        background-color: #e8e8e8;
        font-weight: bold;
      }
      
      .table-container {
        margin-bottom: 20px;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const columnMapping = {
    StartTime: "Start Date & Time",
    EndTime: "Stop Date & Time",
    LineSuper: "Line Supervisor",
    QroNo: "QRO No.",
    DocumentNo: "Document No.",
    IssueType: "Issue Type",
    OutputVariety: "Output Variety",
    OutputeBatch: "Output Batch No.",
    Workworder: "Work Order No.",
    Packingsize: "Packing Size",
    ScreenSize: "Screen Size",
    Mrp: "MRP",
    Inputeqty: "Input Quantity",
    OutputeQty: "Output Quantity",
    RecoveryCal: "Recovery %",
    RemantQty: "Remnant Quantity %",
    LossQty: "Loss Quantity %",
    ApprovalStatus: "Approval Status & Next",
    remark: "Remark",
  };

  // Scrolling text content
  const scrollingText = `${params?.note || ""}`;
  
  return (
    <div className="container">
      <header className="header" style={{ flexWrap: "wrap", gap: "1px", display: "flex", alignItems: "center" }}>
        <button
          className="home-button"
          onClick={() =>
            navigate("/menu", {
              state: { hideModal: true },
            })
          }
        >
          <Home
            className="home-icon"
            size={120}
            style={{
              width: "35px",
              height: "40px",
            }}
          />
        </button>
        
        <div className="controls" style={{ display: "flex", gap: "1px", alignItems: "center" }}>
          <ChevronLeftCircle
            size={40}
            onClick={onPrevious}
            style={{ cursor: "pointer" }}
          />
          {isPaused ? (
            <PlayCircle
              size={40}
              onClick={() => onPause(false)}
              style={{ cursor: "pointer" }}
            />
          ) : (
            <PauseCircle
              size={40}
              onClick={() => onPause(true)}
              style={{ cursor: "pointer" }}
            />
          )}
          <ChevronRightCircle
            size={40}
            onClick={onNext}
            style={{ cursor: "pointer" }}
          />
        </div>
        
        <h1 style={{ fontSize: "clamp(16px, 4vw, 28px)", margin: "5px 0" }}>Real-Time Warehouse Activity</h1>
        
        <div className="header-info">
          <span
            style={{
              background: "linear-gradient(90deg, #0A2A88, #1565C0)",
              color: "#FFFFFF",
              padding: "6px 12px",
              borderRadius: "30px",
              fontSize: "clamp(14px, 2vw, 28px)",
              fontWeight: "800",
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              border: "2px solid rgba(255,255,255,0.7)",
              letterSpacing: "0.5px",
              boxShadow:
                "0 0 10px rgba(255,255,255,0.2), 0 4px 10px rgba(0,0,0,0.4)",
              flexWrap: "wrap",
            }}
          >
            {/* Warehouse Label */}
            <span
              style={{
                color: "#BBDEFB",
                fontWeight: "700",
                textTransform: "uppercase",
                opacity: 0.9,
              }}
            >
              {warehouseLabel}
            </span>

            {/* Divider */}
            <span
              style={{
                width: "2px",
                height: "22px",
                background: "rgba(255,255,255,0.5)",
              }}
            />

            {/* Warehouse Value */}
            <span
              style={{
                color: "#FFFFFF",
                fontSize: "clamp(16px, 3vw, 28px)",
                fontWeight: "bold",
                textShadow: "0 0 8px black, 0 0 12px black",
                wordBreak: "break-word",
                maxWidth: "400px",
              }}
            >
              {displayWarehouse}
            </span>
          </span>
        </div>
        
        <div
          className="date-display"
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
        >
          <span>Current Date:</span>
          <input
            type="text"
            value={currentDate}
            readOnly
            className="current-date"
            style={{
              width: "80px",
              minWidth: "80px",
              padding: "4px 8px",
              fontSize: "14px",
            }}
          />
        </div>
        
        <div className="logo-container">
          <a
            href="https://www.digiopenservices.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/Logo.png"
              alt="Logo"
              className="header-logo"
              style={{
                maxwidth: "110px",
                height: "40px",
                objectFit: "contain",
                borderRadius: "6px",
                margin: "2px 10px",
                cursor: "pointer",
              }}
            />
          </a>
        </div>
      </header>

      {loading && <p className="loading">Loading data, please wait...</p>}
    
      {error && <p className="error">{error}</p>}
      
      {!loading && (!realTimeWarehouseActivity?.data || realTimeWarehouseActivity.data.length === 0) && (
        <div className="no-data">
          <img
            src={noDataImage}
            alt="No data available"
            className="no-data-image"
          />
          <p>No data available for the selected organization.</p>
        </div>
      )}
      
      <div className="table-layout">
        <div className="table-row">
          {!loading && realTimeWarehouseActivity?.data?.length > 0 && (
            <div className="table-container">
              <table className="data-table horizontal-table">
                <thead>
                  {/* <tr>
                    <th style={{ minWidth: "150px" }}>Details</th>
                    {realTimeWarehouseActivity.data.map((item, index) => (
                      <th key={index} style={{ minWidth: "120px" }}>{displayline}</th>
                    ))}
                  </tr> */}
                  <tr>
                    <th>Details</th>
                    {realTimeWarehouseActivity.data.map((item, index) => (
                      <th key={index}>{item.LineNo || "-"}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(columnMapping).map((key, rowIndex) => (
                    <tr key={rowIndex}>
                      <th className="header-column">{columnMapping[key]}</th>
                      {realTimeWarehouseActivity.data.map((item, colIndex) => (
                        <td key={colIndex}>
                          {item[key] !== null && item[key] !== undefined ? item[key] : "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div
        style={{
          position: "fixed",
          bottom: "40px",
          left: 0,
          right: 0,
          color: "#022fe3",
          padding: "12px 0",
          overflow: "hidden",
          whiteSpace: "nowrap",
          fontFamily: "monospace",
          fontSize: "25px",
          fontWeight: "700",
          zIndex: 1000,
        }}
      >
        <div
          style={{
            display: "inline-block",
            animation: "scrollRightToLeft 35s linear infinite",
            whiteSpace: "nowrap",
            paddingLeft: "100%",
          }}
        >
          <span>{scrollingText}</span>
        </div>

        <style>
          {`
      @keyframes scrollRightToLeft {
        0% {
          transform: translateX(0%);
        }
        100% {
          transform: translateX(-100%);
        }
      }
    `}
        </style>
      </div>
    </div>
  );
};

export default RealTimeWarehouseActivity;